/**
 * SupportEdgeTab · v2.6.9 batch BL
 * ================================
 * Free-floating Safety & Support stack pinned to the right edge of the
 * viewport, sitting just ABOVE the PanicButton. Tap the LifeBuoy tab to
 * expand the support stack outward (Contact Dispatch / Rider Support /
 * Safety Concerns); tap outside or press Escape to collapse.
 *
 * Shared between Driver Dashboard and Rider Book-a-Ride. Same right-edge
 * coordinate space as PanicButton so the two stack vertically and never
 * overlap the live GPS map.
 *
 * Spec:
 *   tab color  · indigo (distinct from the red PanicButton tab)
 *   width      · 44 px (same as PanicButton.tab)
 *   position   · top-1/2 minus 64 px (sits 64 px above the PanicButton)
 *   expanded   · slides a 280 px panel out to the LEFT
 *   contacts   · Contact Dispatch (tel) · Rider Support (mail) · Safety (mail)
 */
import { useEffect, useRef, useState } from "react";
import { LifeBuoy, Phone, Mail, ShieldAlert, X as XIcon } from "lucide-react";

// Centralized contact endpoints — kept in sync with SafetySupportCard.
const CONTACTS = [
  {
    label: "Contact Dispatch",
    value: "(901) 459-9448",
    href: "tel:+19014599448",
    Icon: Phone,
    accent: "amber",
    description: "24/7 live dispatch",
    testid: "support-edge-dispatch",
  },
  {
    label: "Rider Support",
    value: "support@bullertaxi.com",
    href: "mailto:support@bullertaxi.com",
    Icon: LifeBuoy,
    accent: "indigo",
    description: "Lost items · billing",
    testid: "support-edge-support",
  },
  {
    label: "Safety Concerns",
    value: "safety@bullertaxi.com",
    href: "mailto:safety@bullertaxi.com",
    Icon: ShieldAlert,
    accent: "red",
    description: "Misconduct · incidents",
    testid: "support-edge-safety",
  },
];

const accentRing = {
  amber: "border-amber-500/50 hover:bg-amber-500/10",
  indigo: "border-indigo-500/50 hover:bg-indigo-500/10",
  red: "border-red-500/50 hover:bg-red-500/10",
};
const accentIcon = {
  amber: "text-amber-300",
  indigo: "text-indigo-300",
  red: "text-red-300",
};

/**
 * @param {object} props
 * @param {boolean} [props.hide]  hide the edge tab entirely (parent gates)
 */
export default function SupportEdgeTab({ hide = false }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  // Close on outside tap / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    // Defer for one tick so the open-click doesn't immediately close.
    const t = setTimeout(() => {
      document.addEventListener("pointerdown", onClick);
      document.addEventListener("keydown", onKey);
    }, 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener("pointerdown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (hide) return null;

  return (
    <div
      ref={panelRef}
      className="fixed top-1/2 right-0 z-40"
      style={{ transform: "translateY(calc(-50% - 64px))" }}
      data-testid="support-edge-anchor"
    >
      <div className="flex items-stretch">
        {/* Expanded panel (slides out to the LEFT) */}
        {open && (
          <div
            className="mr-0 bg-[#121212] border-2 border-indigo-500/50 rounded-l-lg shadow-2xl shadow-indigo-900/40 animate-in slide-in-from-right-4 duration-200 overflow-hidden"
            style={{ width: 268, color: "#E0E0E0" }}
            data-testid="support-edge-panel"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 bg-[#0A0A0A]">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-3.5 w-3.5 text-red-400"/>
                <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-200">Safety & Support</span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="support-edge-close"
                className="text-zinc-500 hover:text-zinc-200"
                aria-label="Close support stack"
              >
                <XIcon className="h-3.5 w-3.5"/>
              </button>
            </div>
            <ul className="p-2 space-y-1.5">
              {CONTACTS.map((c) => {
                const Icon = c.Icon;
                return (
                  <li key={c.testid}>
                    <a
                      href={c.href}
                      data-testid={c.testid}
                      target={c.href.startsWith("tel:") ? undefined : "_blank"}
                      rel={c.href.startsWith("tel:") ? undefined : "noreferrer"}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-2.5 rounded-md border-2 px-2.5 py-2 transition-colors bg-[#1A1A1A] ${accentRing[c.accent]}`}
                    >
                      <div className={`h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0 ${accentIcon[c.accent]}`} style={{ backgroundColor: "#0A0A0A" }}>
                        <Icon className="h-4 w-4"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] uppercase tracking-widest font-bold text-zinc-500">{c.label}</div>
                        <div className="text-[12px] font-extrabold truncate text-zinc-100">{c.value}</div>
                        <div className="text-[9px] text-zinc-500">{c.description}</div>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Always-visible 44px tab — sits on the right edge */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="support-edge-btn"
          aria-label="Open Safety & Support stack"
          title="Safety & Support"
          className={`relative w-11 h-11 flex items-center justify-center text-white shadow-lg rounded-l-md border-l border-y group transition-colors ${open ? "bg-indigo-500 border-indigo-300/60" : "bg-indigo-600 hover:bg-indigo-500 border-indigo-300/40"}`}
        >
          <LifeBuoy className="h-5 w-5 relative z-10 group-active:scale-95 transition-transform"/>
          <span className="sr-only">Safety & Support</span>
        </button>
      </div>
    </div>
  );
}
